import { apiUrl } from "../config/config";

export const LOCAL_TEMPLATE_API_BASE = `${apiUrl}`;

export const normalizeImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (imagePath.startsWith("http://127.0.0.1:8000")) {
    return imagePath.replace("http://127.0.0.1:8000", LOCAL_TEMPLATE_API_BASE);
  }
  if (imagePath.startsWith("http://localhost:8000")) {
    return imagePath.replace("http://localhost:8000", LOCAL_TEMPLATE_API_BASE);
  }
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  if (imagePath.startsWith("/")) {
    return `${apiUrl}${imagePath}`;
  }
  if (imagePath.startsWith("storage/")) {
    return `${apiUrl}/${imagePath}`;
  }
  return `${apiUrl}/storage/${imagePath}`;
};

export const parseMaybeJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const getTemplateCategoryList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.categories)) return payload.categories;
  if (Array.isArray(payload?.template_categories)) return payload.template_categories;
  if (Array.isArray(payload?.templateCategories)) return payload.templateCategories;
  return [];
};

export const getTemplateCategoryDetail = (payload) => {
  if (!payload) return null;
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  if (payload?.template_category) return payload.template_category;
  if (payload?.templateCategory) return payload.templateCategory;
  if (!Array.isArray(payload) && typeof payload === "object") return payload;
  return null;
};

export const getTemplateSubcategories = (category) => {
  const possibleLists = [
    category?.subcategories,
    category?.subCategories,
    category?.sub_categories,
    category?.subcategory,
    category?.sub_category,
    category?.assigned_subcategories,
    category?.assignedSubcategories,
    category?.assigned_sub_categories,
    category?.template_category_subcategories,
    category?.templateCategorySubcategories,
    category?.template_subcategories,
    category?.templateSubcategories,
    category?.template_sub_categories,
  ];

  for (const list of possibleLists) {
    const parsedList = parseMaybeJsonArray(list);
    if (parsedList.length > 0) return parsedList;
  }

  return [];
};

export const getTemplateItemName = (item) =>
  item?.template_name ||
  item?.name ||
  item?.title ||
  item?.subcategory_name ||
  item?.sub_category_name ||
  "";

export const getTemplateImage = (item) =>
  item?.img ||
  item?.image ||
  item?.thumbnail ||
  item?.preview ||
  item?.template_img ||
  item?.template_image ||
  item?.image_path ||
  item?.template?.img ||
  item?.template?.image ||
  item?.template?.thumbnail ||
  item?.template?.preview ||
  item?.template?.image_path ||
  "";

export const getTemplateImages = (item) => {
  const imageValue = getTemplateImage(item);

  if (Array.isArray(imageValue)) {
    return imageValue.filter(Boolean);
  }

  if (typeof imageValue === "string") {
    const trimmedValue = imageValue.trim();

    if (trimmedValue.startsWith("[") && trimmedValue.endsWith("]")) {
      try {
        const parsedImages = JSON.parse(trimmedValue);
        return Array.isArray(parsedImages) ? parsedImages.filter(Boolean) : [];
      } catch (error) {
        return [imageValue];
      }
    }

    return imageValue ? [imageValue] : [];
  }

  return [];
};

export const getTemplateBackgroundColor = (item) =>
  item?.background_color || item?.backgroundColor || item?.template?.background_color || "";

export const getAssignedTemplateList = (payload) => {
  const possibleLists = [
    payload,
    payload?.data,
    payload?.data?.data,
    payload?.templates,
    payload?.assigned_templates,
    payload?.assignedTemplates,
    payload?.template_assigns,
    payload?.templateAssigns,
  ];

  for (const list of possibleLists) {
    if (Array.isArray(list)) {
      return list.map((item) => item?.template || item).filter(Boolean);
    }
  }

  return [];
};

const parseTemplateDesignValue = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }

  return value;
};

export const getTemplateDesignParts = (template) => {
  const rawDesign = parseTemplateDesignValue(template?.design);
  const design =
    rawDesign && typeof rawDesign === "object" && "value" in rawDesign
      ? parseTemplateDesignValue(rawDesign.value)
      : rawDesign;

  if (!design || typeof design !== "object") {
    return {
      Front: [],
      Back: [],
      Right: [],
      Left: [],
    };
  }

  const getPart = (partName) => {
    const lowerPartName = partName.toLowerCase();
    const partValue = design[partName] || design[lowerPartName];
    const parsedPartValue = parseTemplateDesignValue(partValue);

    return Array.isArray(parsedPartValue) ? parsedPartValue : [];
  };

  return {
    Front: getPart("Front"),
    Back: getPart("Back"),
    Right: getPart("Right"),
    Left: getPart("Left"),
  };
};

export const writeTemplateDesignToStorage = (templateId, template) => {
  const templateDesignId = `template_${templateId}`;
  const designParts = getTemplateDesignParts(template);

  localStorage.setItem("designId", templateDesignId);
  localStorage.setItem("retainDesignerObjectsOnNextLoad", "1");

  Object.entries(designParts).forEach(([partName, partData]) => {
    localStorage.setItem(`templateBase_${templateId}_${partName}`, JSON.stringify(partData));
    localStorage.setItem(`${templateDesignId}_${partName}`, JSON.stringify(partData));
    localStorage.removeItem(`${templateDesignId}-${partName}-history`);
  });

  return templateDesignId;
};

export const handleEditTemplateDesign = (template) => {
  const productId = template?.product_id || template?.productId || "";
  const canvasId = template?.canvas_id || template?.canvasId || "";
  const colorId = template?.color_id || template?.colorId || "";
  const templateId = template?.id || Date.now();
  const templateDesignId = writeTemplateDesignToStorage(templateId, template);

  const designerParams = new URLSearchParams();

  if (canvasId) designerParams.set("id", canvasId);
  if (productId) designerParams.set("pid", productId);
  if (colorId) designerParams.set("colorId", colorId);
  designerParams.set("retainDesignId", templateDesignId);
  designerParams.set("templateId", templateId);
  designerParams.set("templateEdit", Date.now().toString());

  window.location.href = `/designer?${designerParams.toString()}`;
};
